/**
 * Champs Puck custom : alignement / justification en icônes, gap en presets S–XL.
 */
import type { ReactNode } from "react";
import { usePuck, type ComponentData } from "@measured/puck";
import {
  AlignStartVertical,
  AlignCenterVertical,
  AlignEndVertical,
  Columns3,
  Info,
} from "lucide-react";
import { Label } from "@/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { CMS_PALETTE_TEXT_INPUT_CLASS } from "@/components/cms/theme/cms-color-input-styles";
import {
  getLayoutGapOptions,
  LAYOUT_GAP_PX,
  type LayoutAlign,
  type LayoutGap,
  type LayoutJustify,
} from "@/lib/cms-puck/layout";
import { useCmsNew } from "@/lib/cms/cms-new-i18n";

function IconToggle({
  label,
  active,
  onClick,
  children,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active}
      className={cn(
        "inline-flex h-9 min-w-9 items-center justify-center rounded-md border px-2 text-slate-600 transition-colors",
        active
          ? "border-slate-900 bg-slate-900 text-white"
          : "border-slate-200 bg-white hover:bg-slate-50",
      )}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

const FLEX_ALIGN_OPTIONS: Array<[LayoutAlign, string, ReactNode]> = [
  ["start", "Haut", <AlignStartVertical key="start" className="h-4 w-4" />],
  ["center", "Centre", <AlignCenterVertical key="center" className="h-4 w-4" />],
  ["end", "Bas", <AlignEndVertical key="end" className="h-4 w-4" />],
  ["stretch", "Étiré", <Columns3 key="stretch" className="h-4 w-4" />],
];

const FLEX_JUSTIFY_OPTIONS: Array<[LayoutJustify, string, ReactNode]> = [
  ["start", "Début", <AlignStartVertical key="j-start" className="h-4 w-4" />],
  ["center", "Centre", <AlignCenterVertical key="j-center" className="h-4 w-4" />],
  ["end", "Fin", <AlignEndVertical key="j-end" className="h-4 w-4" />],
  ["between", "Espacé", <Columns3 key="j-between" className="h-4 w-4" />],
];

const GAP_SHORT: Record<LayoutGap, string> = {
  none: "0",
  sm: "S",
  md: "M",
  lg: "L",
  xl: "XL",
};

type GapCustomFieldName =
  | "gapCustomPx"
  | "gapRowCustomPx"
  | "gapColCustomPx";

export function FlexAlignField({
  value,
  onChange,
  label,
  hideLabel = false,
}: {
  value: LayoutAlign;
  onChange: (value: LayoutAlign) => void;
  label?: string;
  /** Masque le libellé interne (Puck affiche déjà le label du champ). */
  hideLabel?: boolean;
}) {
  const cmsNew = useCmsNew();
  const resolvedLabel =
    label ??
    cmsNew("cms.new.fields.layoutAlign.fieldLabel", "Alignement (transversal)");
  const current = value ?? "stretch";
  return (
    <div className="space-y-2">
      {!hideLabel ? (
        <Label className="text-xs font-medium text-slate-600">
          {resolvedLabel}
        </Label>
      ) : null}
      <div className="flex flex-wrap gap-1.5">
        {FLEX_ALIGN_OPTIONS.map(([v, lbl, icon]) => (
          <IconToggle
            key={v}
            label={cmsNew(`cms.new.fields.layoutAlign.${v}`, lbl)}
            active={current === v}
            onClick={() => onChange(v)}
          >
            {icon}
          </IconToggle>
        ))}
      </div>
    </div>
  );
}

export function FlexJustifyField({
  value,
  onChange,
  label,
  hideLabel = false,
}: {
  value: LayoutJustify;
  onChange: (value: LayoutJustify) => void;
  label?: string;
  hideLabel?: boolean;
}) {
  const cmsNew = useCmsNew();
  const resolvedLabel =
    label ??
    cmsNew("cms.new.fields.layoutJustify.fieldLabel", "Justification (principal)");
  const current = value ?? "start";
  return (
    <div className="space-y-2">
      {!hideLabel ? (
        <Label className="text-xs font-medium text-slate-600">
          {resolvedLabel}
        </Label>
      ) : null}
      <div className="flex flex-wrap gap-1.5">
        {FLEX_JUSTIFY_OPTIONS.map(([v, lbl, icon]) => (
          <IconToggle
            key={v}
            label={cmsNew(`cms.new.fields.layoutJustify.${v}`, lbl)}
            active={current === v}
            onClick={() => onChange(v)}
          >
            <span className="inline-flex rotate-90">{icon}</span>
          </IconToggle>
        ))}
      </div>
    </div>
  );
}

/**
 * Presets gap S/M/L/XL + champ numérique pour valeur exacte.
 * Le px custom est stocké dans une prop sœur (`gapCustomPx`, etc.) via `usePuck`.
 */
export function GapIconField({
  value,
  onChange,
  label,
  customFieldName = "gapCustomPx",
}: {
  value: LayoutGap;
  onChange: (value: LayoutGap) => void;
  label: string;
  customFieldName?: GapCustomFieldName;
}) {
  const cmsNew = useCmsNew();
  // Construit le guide des tailles À CHAQUE RENDU : getLayoutGapOptions() résout
  // les libellés via i18n au moment de l'appel (réactif au chargement de la trad
  // API), contrairement à un const de module qui figerait sur le fallback.
  const gapSizeGuide = getLayoutGapOptions().map((opt) => ({
    short: GAP_SHORT[opt.value],
    px: LAYOUT_GAP_PX[opt.value],
    label: opt.label,
  }));
  const { selectedItem, dispatch, getSelectorForId, getItemById } = usePuck();
  // Horizontal/vertical non définis → héritent de l'uniforme (`gap`), cf. resolveGapLayout.
  const preset = (value ??
    (selectedItem?.props?.gap as LayoutGap | undefined) ??
    "md") as LayoutGap;
  const customPx = selectedItem?.props?.[customFieldName] as
    | number
    | null
    | undefined;

  // Une seule `replace` pour preset + px : deux dispatch successifs écrasaient
  // le preset (le 2e repart d'un `node` obsolète) → les boutons S/M/L ne
  // changeaient rien quand un px précis était défini.
  const patchProps = (patch: Record<string, unknown>) => {
    const itemId = selectedItem?.props?.id as string | undefined;
    if (!itemId) return;
    const selector = getSelectorForId(itemId);
    const node = getItemById(itemId) as ComponentData | undefined;
    if (!selector || !node) return;
    dispatch({
      type: "replace",
      destinationIndex: selector.index,
      destinationZone: selector.zone,
      data: { ...node, props: { ...node.props, ...patch } },
    });
  };
  const presetFieldName = customFieldName.replace("CustomPx", "");

  const setCustomPx = (raw: string) => {
    const n = raw === "" ? null : Number(raw);
    const next =
      n != null && Number.isFinite(n) && n >= 0 ? Math.round(n) : null;
    patchProps({ [customFieldName]: next });
  };

  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-2">
        <div className="flex items-center gap-1.5">
          <Label className="text-xs font-medium text-slate-600">{label}</Label>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="inline-flex h-5 w-5 items-center justify-center rounded text-slate-400 hover:text-slate-600"
                aria-label={cmsNew("cms.new.auto.label.guide_des_tailles", "Guide des tailles")}
              >
                <Info className="h-3.5 w-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-slate-700">
              <p className="mb-2 font-medium">{cmsNew("cms.new.auto.text.guide_des_tailles", "Guide des tailles")}</p>
              <ul className="space-y-1.5 text-[11px] leading-snug">
                {gapSizeGuide.map((entry) => (
                  <li
                    key={entry.short}
                    className="flex items-center justify-between gap-6 tabular-nums"
                  >
                    <span>
                      <span className="inline-block w-5 font-semibold">
                        {entry.short}
                      </span>
                      <span className="text-slate-500">{entry.label}</span>
                    </span>
                    <span className="font-medium">{entry.px} px</span>
                  </li>
                ))}
              </ul>
            </TooltipContent>
          </Tooltip>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {getLayoutGapOptions().map((opt) => (
            <IconToggle
              key={opt.value}
              label={`${GAP_SHORT[opt.value]} — ${LAYOUT_GAP_PX[opt.value]} px`}
              active={preset === opt.value && customPx == null}
              onClick={() => {
                if (customPx != null) {
                  patchProps({
                    [presetFieldName]: opt.value,
                    [customFieldName]: null,
                  });
                } else {
                  onChange(opt.value);
                }
              }}
            >
              <span className="text-[10px] font-semibold tabular-nums">
                {GAP_SHORT[opt.value]}
              </span>
            </IconToggle>
          ))}
          <div className="flex items-center gap-1.5 pl-1">
            <Label className="shrink-0 text-[10px] font-medium text-slate-500">
              {cmsNew("cms.new.fields.layoutGap.precise", "Précis")}
            </Label>
            <input
              type="number"
              min={0}
              step={1}
              value={customPx ?? ""}
              placeholder={cmsNew("cms.new.auto.placeholder.px", "px")}
              title={cmsNew("cms.new.auto.title.valeur_exacte_en_pixels_remplace_le_preset", "Valeur exacte en pixels (remplace le preset)")}
              className={cn(
                CMS_PALETTE_TEXT_INPUT_CLASS,
                "h-9 w-20 text-center tabular-nums",
              )}
              onChange={(e) => setCustomPx(e.target.value)}
            />
            <span className="text-[10px] text-slate-400">{cmsNew("cms.new.auto.text.px", "px")}</span>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
